import type { StaffWorkday } from "../types";

/** Day labels: 0=Sun, 1=Mon, ..., 6=Sat */
const DAY_LABELS: Record<number, string> = {
	0: "Yakshanba",
	1: "Dushanba",
	2: "Seshanba",
	3: "Chorshanba",
	4: "Payshanba",
	5: "Juma",
	6: "Shanba",
};

/** Group workdays by start/end time slot */
function groupByTimeSlot(active: StaffWorkday[]): Map<string, number[]> {
	const byTime = new Map<string, number[]>();
	for (const w of active) {
		const key = `${w.start ?? ""}|${w.end ?? ""}`;
		const days = byTime.get(key) ?? [];
		if (w.dayNumber !== undefined && w.dayNumber !== null) {
			days.push(w.dayNumber);
		}
		byTime.set(key, days);
	}
	return byTime;
}

/** Build display string for one time slot */
function formatSlot(key: string, days: number[]): string {
	const [start, end] = key.split("|");
	const timeStr =
		start && end ? `${formatTime(start)}–${formatTime(end)}` : start ? formatTime(start) : "";
	const sorted = [...new Set(days)].sort((a, b) => a - b);
	const dayStr = formatDayRange(sorted);
	return dayStr + (timeStr ? ` ${timeStr}` : "");
}

/**
 * Format staff workdays into a readable string for display.
 * e.g. "Dushanba–Juma 08:00–18:00" or "Dushanba, Chorshanba 09:00–17:00"
 */
export function formatWorkdaysDisplay(workdays: StaffWorkday[] | null | undefined): string {
	if (!workdays?.length) {
		return "—";
	}

	const active = workdays.filter((w) => w.isWorkday !== false);
	if (!active.length) {
		return "—";
	}

	const byTime = groupByTimeSlot(active);
	const parts: string[] = [];
	for (const [key, days] of byTime) {
		parts.push(formatSlot(key, days));
	}

	return parts.join("; ") || "—";
}

function formatTime(t: string): string {
	// Handle "HH:mm:ss" or "HH:mm" format
	const parts = t.split(":");
	return parts.slice(0, 2).join(":");
}

function formatDayRange(days: number[]): string {
	if (!days.length) {
		return "";
	}
	if (days.length === 1) {
		return DAY_LABELS[days[0]] ?? String(days[0]);
	}

	// Check for consecutive range (e.g. 1,2,3,4,5 = Mon-Fri)
	const sorted = [...days].sort((a, b) => a - b);
	const isConsecutive = sorted.every((d, i) => i === 0 || d === sorted[i - 1] + 1);
	if (isConsecutive && sorted.length >= 2) {
		return `${DAY_LABELS[sorted[0]] ?? sorted[0]}–${DAY_LABELS[sorted[sorted.length - 1]] ?? sorted[sorted.length - 1]}`;
	}

	return sorted.map((d) => DAY_LABELS[d] ?? d).join(", ");
}
